import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import {
  AppRole,
  TagLayerValues,
  TagStatus,
  TaxonomyRunStatus,
  type ICanonicalTag,
  type ITagAliasDecision,
  type ITaxonomySyncRun,
} from "@monorepo/core";

import {
  ADMIN_SIDEBAR_DESKTOP_WIDTH,
  default as AdminSidebar,
} from "../components/admin-sidebar.component";
import AppHeader from "../components/app-header.component";
import useAuth from "../hooks/auth.hook";
import {
  usePendingTagAliasDecisions,
  useTagAliasBootstrapAdminActions,
  useTagAliasBootstrapAdminState,
} from "../hooks/taxonomy-admin.hook";
import { displayFontFamily } from "../theme/mtgTheme";

const PENDING_PAGE_SIZE = 15;

type TagLayer = (typeof TagLayerValues)[number];

const ACTIVE_ALIAS_BOOTSTRAP_RUN_STATUSES = new Set<string>([
  TaxonomyRunStatus.PENDING,
  TaxonomyRunStatus.SCANNING,
  TaxonomyRunStatus.DETAILING,
  TaxonomyRunStatus.PUBLISHING,
]);

const SummaryMetricCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => (
  <Card sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontFamily: displayFontFamily }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const formatTimestamp = (value?: string) =>
  value
    ? new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const getRunMetric = (
  run: ITaxonomySyncRun | undefined,
  metricKey: string,
): number => run?.metrics[metricKey] ?? 0;

const buildProgressValue = (completed: number, queued: number) =>
  queued > 0 ? Math.min(100, Math.round((completed / queued) * 100)) : 0;

const formatRunTypeLabel = (runStatus?: string) => {
  switch (runStatus) {
    case TaxonomyRunStatus.PENDING:
      return "Queued";
    case TaxonomyRunStatus.SCANNING:
      return "Scanning";
    case TaxonomyRunStatus.DETAILING:
      return "Detailing";
    case TaxonomyRunStatus.AWAITING_REVIEW:
      return "Awaiting Review";
    case TaxonomyRunStatus.PUBLISHING:
      return "Publishing";
    case TaxonomyRunStatus.COMPLETED:
      return "Completed";
    case TaxonomyRunStatus.FAILED:
      return "Failed";
    default:
      return runStatus ?? "Idle";
  }
};

const formatDecisionStatus = (decision: ITagAliasDecision) => {
  switch (decision.decisionStatus) {
    case "AUTO_APPROVED":
      return "Auto-approved";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    default:
      return "Pending";
  }
};

const getDecisionChipColor = (decision: ITagAliasDecision) => {
  switch (decision.decisionStatus) {
    case "AUTO_APPROVED":
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "default";
    default:
      return "warning";
  }
};

const sortCanonicalTags = (tags: readonly ICanonicalTag[]) =>
  tags
    .slice()
    .sort(
      (left, right) =>
        left.name.localeCompare(right.name) ||
        left.slug.localeCompare(right.slug),
    );

const TagAliasBootstrapAdminPage: FC = () => {
  const { isAdmin, setViewRole, viewRole } = useAuth();
  const adminStateQuery = useTagAliasBootstrapAdminState();
  const {
    startBootstrapMutation,
    approveTagAliasDecisionMutation,
    rejectTagAliasDecisionMutation,
    publishTagAliasSnapshotMutation,
  } = useTagAliasBootstrapAdminActions();

  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] =
    useState<ITagAliasDecision | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<TagLayer>("FUNCTIONAL");
  const [selectedSlug, setSelectedSlug] = useState("");

  const activeCursor = pageCursors[pageIndex];
  const trimmedSearch = search.trim();
  const pendingDecisionQuery = usePendingTagAliasDecisions({
    cursor: activeCursor,
    pageSize: PENDING_PAGE_SIZE,
    search: trimmedSearch.length > 0 ? trimmedSearch : undefined,
  });

  useEffect(() => {
    setPageIndex(0);
    setPageCursors([undefined]);
  }, [trimmedSearch]);

  useEffect(() => {
    if (!isAdmin && viewRole === AppRole.Admin) {
      setViewRole(AppRole.Member);
    }
  }, [isAdmin, setViewRole, viewRole]);

  const runs = adminStateQuery.data?.runs ?? [];
  const latestRun = runs[0];
  const activeRun = runs.find((run) =>
    ACTIVE_ALIAS_BOOTSTRAP_RUN_STATUSES.has(run.runStatus),
  );
  const workspace = adminStateQuery.data?.workspace;
  const activeCanonicalTags = useMemo(
    () =>
      sortCanonicalTags(
        (adminStateQuery.data?.currentCanonicalSnapshot?.tags ?? []).filter(
          (tag) => tag.status === TagStatus.ACTIVE,
        ),
      ),
    [adminStateQuery.data?.currentCanonicalSnapshot?.tags],
  );
  const canonicalTagsByLayer = useMemo(
    () =>
      TagLayerValues.reduce<Record<TagLayer, ICanonicalTag[]>>(
        (acc, layer) => ({
          ...acc,
          [layer]: activeCanonicalTags.filter((tag) => tag.layer === layer),
        }),
        {
          FUNCTIONAL: [],
          TRIBAL: [],
          THEME: [],
        },
      ),
    [activeCanonicalTags],
  );
  const availableSlugs = canonicalTagsByLayer[selectedLayer] ?? [];

  useEffect(() => {
    if (availableSlugs.length === 0) {
      setSelectedSlug("");
      return;
    }

    if (!availableSlugs.some((tag) => tag.slug === selectedSlug)) {
      setSelectedSlug(availableSlugs[0]!.slug);
    }
  }, [availableSlugs, selectedSlug]);

  const pendingItems = pendingDecisionQuery.data?.items ?? [];
  const canStartBootstrap =
    Boolean(adminStateQuery.data?.currentCanonicalSnapshot) && !activeRun;
  const canPublish =
    latestRun?.runStatus === TaxonomyRunStatus.AWAITING_REVIEW &&
    (workspace?.pendingDecisionCount ?? 0) === 0;
  const startError =
    startBootstrapMutation.error?.message ??
    publishTagAliasSnapshotMutation.error?.message ??
    approveTagAliasDecisionMutation.error?.message ??
    rejectTagAliasDecisionMutation.error?.message;

  const openApprovalDialog = (decision: ITagAliasDecision) => {
    const fallbackLayer = (TagLayerValues.find(
      (layer) => canonicalTagsByLayer[layer].length > 0,
    ) ?? "FUNCTIONAL") as TagLayer;
    const nextLayer =
      decision.canonicalLayer && canonicalTagsByLayer[decision.canonicalLayer]
        ? (decision.canonicalLayer as TagLayer)
        : fallbackLayer;
    const nextTags = canonicalTagsByLayer[nextLayer];

    setSelectedDecision(decision);
    setSelectedLayer(nextLayer);
    setSelectedSlug(
      decision.canonicalSlug &&
        nextTags.some((tag) => tag.slug === decision.canonicalSlug)
        ? decision.canonicalSlug
        : nextTags[0]?.slug ?? "",
    );
    setApprovalDialogOpen(true);
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setSelectedDecision(null);
  };

  const handleApprove = async () => {
    if (!selectedDecision || !selectedSlug) {
      return;
    }

    await approveTagAliasDecisionMutation.mutateAsync({
      source: selectedDecision.source,
      sourceNamespace: selectedDecision.sourceNamespace,
      rawKey: selectedDecision.rawKey,
      canonicalLayer: selectedLayer,
      canonicalSlug: selectedSlug,
    });
    closeApprovalDialog();
  };

  const handleReject = async (decision: ITagAliasDecision) => {
    await rejectTagAliasDecisionMutation.mutateAsync({
      source: decision.source,
      sourceNamespace: decision.sourceNamespace,
      rawKey: decision.rawKey,
    });
  };

  const handleNextPage = () => {
    const nextCursor = pendingDecisionQuery.data?.nextCursor;
    if (!nextCursor) {
      return;
    }

    setPageCursors((previous) => {
      if (previous[pageIndex + 1] === nextCursor) {
        return previous;
      }

      const next = previous.slice(0, pageIndex + 1);
      next.push(nextCursor);
      return next;
    });
    setPageIndex((previous) => previous + 1);
  };

  const handlePreviousPage = () => {
    setPageIndex((previous) => Math.max(0, previous - 1));
  };

  if (!isAdmin && viewRole !== AppRole.Admin) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <AppHeader />
      <Box
        sx={(theme) => ({
          flex: 1,
          minHeight: 0,
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            lg: `${ADMIN_SIDEBAR_DESKTOP_WIDTH}px minmax(0, 1fr)`,
          },
          alignItems: "stretch",
          overflow: "hidden",
          background: `radial-gradient(circle at top, ${theme.palette.primary.main}14 0%, transparent 42%), radial-gradient(circle at bottom right, ${theme.palette.secondary.main}18 0%, transparent 34%), ${theme.palette.background.default}`,
        })}
      >
        <AdminSidebar activeView="tag-alias-bootstrap" />
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              <Stack spacing={1}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: "0.16em", color: "text.secondary" }}
                >
                  Taxonomy Admin
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontFamily: displayFontFamily, maxWidth: 720 }}
                >
                  Tag Alias Bootstrap
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
                  Crawl Tagger card tags against the published canonical
                  taxonomy, review unresolved alias mappings, and publish the
                  next alias snapshot once every pending decision is resolved.
                </Typography>
              </Stack>

              {startError ? <Alert severity="error">{startError}</Alert> : null}

              {!adminStateQuery.data?.currentCanonicalSnapshot ? (
                <Alert severity="warning">
                  Publish a canonical tag snapshot before starting alias
                  bootstrap. Alias review uses the current canonical taxonomy as
                  its approval target.
                </Alert>
              ) : null}

              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={2}
                sx={{ alignItems: { xl: "stretch" } }}
              >
                <Box sx={{ flex: 1 }}>
                  <SummaryMetricCard
                    label="Latest Run"
                    value={formatRunTypeLabel(latestRun?.runStatus)}
                    helper={
                      latestRun
                        ? `Started ${formatTimestamp(latestRun.startedAt)}`
                        : "No alias bootstrap run yet"
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SummaryMetricCard
                    label="Pending Review"
                    value={String(workspace?.pendingDecisionCount ?? 0)}
                    helper="Pending alias decisions in the latest bootstrap run"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SummaryMetricCard
                    label="Current Alias Snapshot"
                    value={
                      adminStateQuery.data?.currentAliasSnapshot?.metadata
                        .version ?? "Unpublished"
                    }
                    helper={
                      adminStateQuery.data?.currentAliasSnapshot?.metadata
                        .publishedAt
                        ? `Published ${formatTimestamp(
                            adminStateQuery.data?.currentAliasSnapshot.metadata
                              .publishedAt,
                          )}`
                        : "No alias snapshot has been published yet"
                    }
                  />
                </Box>
              </Stack>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="h6">Workflow</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start a bootstrap run, wait for the crawl and detail
                        stages to finish, then review pending alias mappings and
                        publish the snapshot.
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => startBootstrapMutation.mutate()}
                        disabled={
                          !canStartBootstrap || startBootstrapMutation.isLoading
                        }
                      >
                        Start Alias Bootstrap
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() =>
                          latestRun &&
                          publishTagAliasSnapshotMutation.mutate({
                            runId: latestRun.runId,
                          })
                        }
                        disabled={
                          !latestRun ||
                          !canPublish ||
                          publishTagAliasSnapshotMutation.isLoading
                        }
                      >
                        Publish Alias Snapshot
                      </Button>
                    </Stack>
                  </Stack>

                  {latestRun ? (
                    <Stack spacing={2} sx={{ mt: 3 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={700}>
                          Latest run: {latestRun.runId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Updated {formatTimestamp(latestRun.updatedAt)}
                        </Typography>
                        {latestRun.failureReason ? (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {latestRun.failureReason}
                          </Alert>
                        ) : null}
                      </Stack>

                      <Stack spacing={1.5}>
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.75 }}
                          >
                            <Typography variant="body2">Page scans</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getRunMetric(
                                latestRun,
                                "pageScanCompletedCount",
                              )}
                              /{getRunMetric(latestRun, "pageScanQueuedCount")}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={buildProgressValue(
                              getRunMetric(latestRun, "pageScanCompletedCount"),
                              getRunMetric(latestRun, "pageScanQueuedCount"),
                            )}
                          />
                        </Box>
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.75 }}
                          >
                            <Typography variant="body2">
                              Detail fetches
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getRunMetric(latestRun, "detailCompletedCount")}/
                              {getRunMetric(latestRun, "detailQueuedCount")}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={buildProgressValue(
                              getRunMetric(latestRun, "detailCompletedCount"),
                              getRunMetric(latestRun, "detailQueuedCount"),
                            )}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  ) : null}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="h6">Pending Alias Review</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review only the latest alias bootstrap run. Approvals
                        can keep the proposed target or override it to any
                        active canonical tag.
                      </Typography>
                    </Stack>
                    <TextField
                      size="small"
                      label="Search pending aliases"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      sx={{ minWidth: { md: 320 } }}
                    />
                  </Stack>

                  {pendingDecisionQuery.isLoading ? (
                    <LinearProgress sx={{ mt: 3 }} />
                  ) : null}

                  <Table sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Raw Tag</TableCell>
                        <TableCell>Proposed Mapping</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Examples</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography color="text.secondary">
                              No pending alias decisions match the current
                              filter.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {pendingItems.map((decision) => (
                        <TableRow
                          key={`${decision.source}:${decision.sourceNamespace}:${decision.rawKey}`}
                          hover
                        >
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography fontWeight={700}>
                                {decision.rawName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {decision.rawKey}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {decision.source}:{decision.sourceNamespace}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.75}>
                              <Typography variant="body2">
                                {decision.canonicalLayer &&
                                decision.canonicalSlug
                                  ? `${decision.canonicalLayer}:${decision.canonicalSlug}`
                                  : "No proposed match"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {decision.reasoningSummary}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={formatDecisionStatus(decision)}
                              color={getDecisionChipColor(decision)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {decision.exampleOracleIds.join(", ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openApprovalDialog(decision)}
                                disabled={
                                  approveTagAliasDecisionMutation.isLoading ||
                                  activeCanonicalTags.length === 0
                                }
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                color="inherit"
                                onClick={() => handleReject(decision)}
                                disabled={
                                  rejectTagAliasDecisionMutation.isLoading
                                }
                              >
                                Reject
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Page {pageIndex + 1}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        onClick={handlePreviousPage}
                        disabled={pageIndex === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        onClick={handleNextPage}
                        disabled={!pendingDecisionQuery.data?.nextCursor}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6">Run History</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Recent alias bootstrap runs remain visible here even after a
                    newer run starts.
                  </Typography>
                  <Table sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Run</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Completed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography color="text.secondary">
                              No alias bootstrap runs have been recorded yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {runs.map((run) => (
                        <TableRow key={run.runId}>
                          <TableCell>{run.runId}</TableCell>
                          <TableCell>
                            {formatRunTypeLabel(run.runStatus)}
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(run.startedAt)}
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(run.completedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Stack>
          </Container>
        </Box>
      </Box>

      <Dialog
        open={approvalDialogOpen}
        onClose={closeApprovalDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Tag Alias</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Reviewers can override the proposed mapping before approval.
            </Alert>
            <TextField
              label="Canonical layer"
              select
              value={selectedLayer}
              onChange={(event) =>
                setSelectedLayer(event.target.value as TagLayer)
              }
            >
              {TagLayerValues.filter(
                (layer) => canonicalTagsByLayer[layer].length > 0,
              ).map((layer) => (
                <MenuItem key={layer} value={layer}>
                  {layer}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Canonical tag"
              select
              value={selectedSlug}
              onChange={(event) => setSelectedSlug(event.target.value)}
              disabled={availableSlugs.length === 0}
              helperText={
                availableSlugs.length === 0
                  ? "No active canonical tags are available for this layer."
                  : undefined
              }
            >
              {availableSlugs.map((tag) => (
                <MenuItem key={tag.slug} value={tag.slug}>
                  {tag.name} ({tag.slug})
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={!selectedDecision || !selectedSlug}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagAliasBootstrapAdminPage;
